// src/components/Header.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Box,
  useTheme,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { getAdminFromToken, clearToken } from '../utils/auth';
import { getAdminProfile } from '../services/api'; // <- fetch the up-to-date profile

export default function Header({ sidebarCompact = false }) {
  const navigate = useNavigate();
  const theme = useTheme();

  // seed with token info (if any) so header shows immediately
  const tokenAdmin = getAdminFromToken?.() || {};
  const [admin, setAdmin] = useState(tokenAdmin);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  // clock state
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let mounted = true;
    // fetch the authoritative profile (includes profileImageUrl)
    (async () => {
      try {
        const res = await getAdminProfile();
        // normalize: response might be { data: { ... } } or { ... }
        const payload = res?.data ?? res;
        if (!mounted) return;

        // backend controller should return profileImageUrl field
        // If it returns user: { ... }, handle that too
        const profile = payload?.user ?? payload;
        if (profile) {
          setAdmin((prev) => ({ ...prev, ...profile }));
        }
      } catch (err) {
        // non-fatal — keep tokenAdmin if fetch fails
        // console.warn('Failed to fetch admin profile', err);
      }
    })();
    return () => (mounted = false);
  }, []);

  useEffect(() => {
    // update every second
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const logout = () => {
    clearToken();
    navigate('/login');
  };

  // prefer profileImageUrl returned by backend; fall back to profileImage or initial
  const avatarSrc = admin?.profileImageUrl || admin?.profileImage || undefined;
  const initial = (admin?.name && admin.name.length > 0) ? admin.name[0].toUpperCase() : 'A';

  // Formatting: use Asia/Kolkata timezone. Change to undefined to use browser timezone.
  const timeZone = 'Asia/Kolkata';

  const timeFormatter = new Intl.DateTimeFormat('en-IN', {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
    timeZone,
  });

  const dateFormatter = new Intl.DateTimeFormat('en-IN', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone,
  });

  const timeString = timeFormatter.format(now);
  const dateString = dateFormatter.format(now);

  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={1}
      sx={{
        ml: sidebarCompact ? 72 : 260,
        width: sidebarCompact ? 'calc(100% - 72px)' : 'calc(100% - 260px)',
        background: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        zIndex: (t) => t.zIndex.drawer + 1,
      }}
    >
      {/* Change to space-between so left: clock, right: profile/actions */}
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3 }}>
        {/* LEFT: Time & Date */}
<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
  <Box sx={{ textAlign: 'left', mr: 1 }}>
    <Typography
      variant="h6"
      sx={{ fontWeight: 800, color: '#fff', lineHeight: 1 }}
    >
      {timeString}
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: '#e5e7eb', fontWeight: 500 }}
    >
      {dateString}
    </Typography>
  </Box>
</Box>


        {/* RIGHT: notifications + profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Notifications">
            <IconButton
              size="small"
              aria-label="notifications"
              sx={{ '&:hover': { bgcolor: theme.palette.action.hover } }}
            >
              <Badge badgeContent={2} color="error">
                <NotificationsIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>

          <Box
            role="button"
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              p: 0.5,
              borderRadius: 1,
              '&:hover': { bgcolor: theme.palette.action.hover },
            }}
            onClick={handleMenuOpen}
          >
            <Avatar
              alt={admin?.name || 'Admin'}
              src={avatarSrc}
              sx={{ width: 32, height: 32, bgcolor: avatarSrc ? 'transparent' : 'primary.main' }}
            >
              { !avatarSrc && initial }
            </Avatar>

            <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1 }}>
                {admin?.name || 'Admin User'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {admin?.email || 'admin@domain.com'}
              </Typography>
            </Box>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{ elevation: 3, sx: { minWidth: 220 } }}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/settings/profile');
              }}
            >
              <AccountCircleIcon fontSize="small" sx={{ mr: 1 }} />
              Profile
            </MenuItem>

            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/settings');
              }}
            >
              <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
              Settings
            </MenuItem>

            <Divider />

            <MenuItem
              onClick={() => {
                handleMenuClose();
                logout();
              }}
              sx={{ color: 'error.main' }}
            >
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
